import { useEffect, useState } from 'react'

import type { GetOrderCancellationResponse } from '@fourthwall/services/dist/OrderApiClient'
import type { NonCancellableOrderOffer, OrderOffer } from '@fourthwall/services/dist/models/order'

import { useOrderCancellationQuery } from '../queries/useOrderCancellationQuery'

const getCancellableOffers = (
  offers?: OrderOffer[],
  cancellableItems?: GetOrderCancellationResponse['cancellableItems'],
) =>
  cancellableItems
    ?.map((cancellableItem) => {
      const offer = offers?.find((offer) => offer.variantId === cancellableItem.variantId)

      return offer ? { ...offer, quantity: cancellableItem.quantity } : undefined
    })
    ?.filter(Boolean) as OrderOffer[]

const getNonCancellableOffers = (
  offers?: OrderOffer[],
  nonCancellableItems?: GetOrderCancellationResponse['nonCancellableItems'],
) =>
  nonCancellableItems
    ?.map((nonCancellableItem) => {
      const offer = offers?.find((offer) => offer.variantId === nonCancellableItem.variantId)

      return offer
        ? {
            ...offer,
            quantity: nonCancellableItem.quantity,
            nonCancellableReason: nonCancellableItem.reason,
          }
        : undefined
    })
    ?.filter(Boolean) as NonCancellableOrderOffer[]

export const useCancellationOffers = (orderId: string, offers?: OrderOffer[]) => {
  const { orderCancellationQuery, invalidateOrderCancellationQuery } = useOrderCancellationQuery([
    orderId,
  ])
  const [cancellableOffers, setCancellableOffers] = useState<OrderOffer[]>()
  const nonCancellableOffers = getNonCancellableOffers(
    offers,
    orderCancellationQuery.data?.nonCancellableItems,
  )

  useEffect(() => {
    setCancellableOffers(
      getCancellableOffers(offers, orderCancellationQuery.data?.cancellableItems),
    )
  }, [offers, orderCancellationQuery.data?.cancellableItems])

  return {
    cancellableOffers,
    nonCancellableOffers,
    partiallyCancellable: !!orderCancellationQuery.data?.partiallyCancellable,
    fullyCancellable: !!orderCancellationQuery.data?.fullyCancellable,
    orderCancellationQuery,
    invalidateOrderCancellationQuery,
  }
}
